import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUturnLeftIcon,
  CalendarIcon,
  MicrophoneIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  DocumentDuplicateIcon,
  UserGroupIcon,
  TableCellsIcon,
  ViewColumnsIcon,
  XMarkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import scheduleService, { errText } from '../../services/scheduleService';
import { isAdmin } from '../../utils/permissions';
import RosterView from './RosterView';
import TimelineView from './TimelineView';
import DayView from './DayView';
import BlockEditor from './BlockEditor';
import GroupsModal from './GroupsModal';
import DoctorFilter from './DoctorFilter';
import AssistantPanel from '../../components/assistant/AssistantPanel';
import {
  addDays,
  colourMap,
  doctorName,
  findConflicts,
  fmtDayName,
  fmtDayNum,
  fmtWeekRange,
  fromISO,
  startOfWeek,
  tint,
  todayISO,
  weekDays,
} from './scheduleUtils';

const EMPTY = { doctors: [], hospitals: [], groups: [], blocks: [], me: null };
const VIEW_KEY = 'scheduleView';
const VIEWS = [
  { key: 'roster', label: 'Roster', Icon: TableCellsIcon },
  { key: 'timeline', label: 'Timeline', Icon: ViewColumnsIcon },
  { key: 'day', label: 'Day', Icon: CalendarIcon },
];

const readView = () => {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return VIEWS.some((x) => x.key === v) ? v : 'roster';
  } catch {
    return 'roster';
  }
};
const saveView = (v) => {
  try { localStorage.setItem(VIEW_KEY, v); } catch { /* preference only */ }
};

/**
 * Theatre Schedule — Use Case 1.1: Weekly Group Scheduling.
 *
 * Three views over the same sessions:
 *   Roster   — doctors down the side, days across (a staff-rostering board).
 *   Timeline — hours down the side, days across, doctors overlaid in colour.
 *   Day      — one day, a column per doctor (drill in by clicking a day).
 * Clicking a doctor's name focuses every view on just that doctor.
 */
const Schedule = () => {
  const admin = isAdmin();
  const me = parseInt(localStorage.getItem('uid'), 10);
  const navigate = useNavigate();

  const [weekStart, setWeekStart] = useState(() => startOfWeek(todayISO()));
  const [focusDay, setFocusDay] = useState(todayISO);
  const [view, setView] = useState(readView);
  const [lastWeekView, setLastWeekView] = useState(() => (readView() === 'timeline' ? 'timeline' : 'roster'));
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scope, setScope] = useState('all'); // 'all' | 'me' | `g${gid}`
  const [hidden, setHidden] = useState(() => new Set());
  const [editor, setEditor] = useState(null); // { draft, isNew }
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [toast, setToast] = useState(null); // { text, tone }

  const load = useCallback(async () => {
    setError('');
    try {
      setData({ ...EMPTY, ...(await scheduleService.week(weekStart)) });
    } catch (err) {
      setError(errText(err));
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------
  const today = todayISO();
  const days = useMemo(() => weekDays(weekStart), [weekStart]);
  const colours = useMemo(() => colourMap(data.doctors), [data.doctors]);
  const hospitalsById = useMemo(() => Object.fromEntries(data.hospitals.map((h) => [h.hid, h])), [data.hospitals]);
  const conflicts = useMemo(() => findConflicts(data.blocks), [data.blocks]);
  const iAmDoctor = data.doctors.some((d) => d.uid === me);

  // The server says whose sessions this user may edit: null = everyone (admin),
  // otherwise themselves plus partners they've been granted.
  const editableSet = useMemo(() => new Set(data.me?.editable || []), [data.me]);
  const canEdit = useCallback((uid) => admin || data.me?.editable === null || editableSet.has(uid), [admin, data.me, editableSet]);
  const editableDoctors = data.doctors.filter((d) => canEdit(d.uid));
  const canCreate = editableDoctors.length > 0;

  const scopedDoctors = useMemo(() => {
    if (scope === 'me') return data.doctors.filter((d) => d.uid === me);
    const group = data.groups.find((g) => `g${g.gid}` === scope);
    return group ? data.doctors.filter((d) => group.members.includes(d.uid)) : data.doctors;
  }, [scope, data.doctors, data.groups, me]);

  // A deleted group falls back to everyone.
  useEffect(() => {
    if (scope.startsWith('g') && !data.groups.some((g) => `g${g.gid}` === scope)) setScope('all');
  }, [scope, data.groups]);

  const visibleDoctors = scopedDoctors.filter((d) => !hidden.has(d.uid));
  const visibleUids = new Set(visibleDoctors.map((d) => d.uid));
  const visibleBlocks = data.blocks.filter((b) => visibleUids.has(b.buid) && b.bdate >= days[0] && b.bdate <= days[6]);
  const focused = scopedDoctors.length > 1 && visibleDoctors.length === 1 ? visibleDoctors[0] : null;

  // The hospital a doctor uses most — pre-filled on new sessions.
  const favouriteHospital = (buid) => {
    const counts = {};
    data.blocks.forEach((b) => {
      if (b.buid === buid && b.bhid) counts[b.bhid] = (counts[b.bhid] || 0) + 1;
    });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return top ? parseInt(top[0], 10) : null;
  };

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------
  const changeView = (v) => {
    if (v === view) return;
    if (v === 'day') {
      setLastWeekView(view);
      setFocusDay(days.includes(focusDay) ? focusDay : days.includes(today) ? today : weekStart);
    }
    setView(v);
    saveView(v);
  };

  const openDay = (day) => {
    if (view !== 'day') setLastWeekView(view);
    setFocusDay(day);
    setWeekStart(startOfWeek(day));
    setView('day');
    saveView('day');
  };

  const goToDay = (day) => {
    setFocusDay(day);
    setWeekStart(startOfWeek(day));
  };

  const step = (n) => {
    if (view === 'day') goToDay(addDays(focusDay, n));
    else setWeekStart((w) => addDays(w, 7 * n));
  };
  const goToday = () => goToDay(todayISO());

  const toggleDoctor = (uid) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  const soloDoctor = (uid) => setHidden(new Set(scopedDoctors.filter((d) => d.uid !== uid).map((d) => d.uid)));

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  const openNew = (partial = {}) => {
    if (!canCreate) return;
    const pick = (uid) => editableDoctors.some((d) => d.uid === uid);
    const buid = pick(partial.buid)
      ? partial.buid
      : pick(me) ? me : (visibleDoctors.find((d) => canEdit(d.uid)) || editableDoctors[0]).uid;
    const defaultDate = view === 'day' ? focusDay : days.includes(today) ? today : weekStart;
    setEditor({
      isNew: true,
      draft: {
        bid: null,
        buid,
        btype: 'theatre',
        bdate: partial.bdate || defaultDate,
        bstart: partial.bstart || '07:00',
        bend: partial.bend || '13:00',
        bhid: favouriteHospital(buid),
        btheatre: '',
        bsurgeon: '',
        bsurgeon_id: null,
        bnotes: '',
        patients: [],
      },
    });
  };

  const openEdit = (b) =>
    setEditor({
      isNew: false,
      draft: {
        ...b,
        bstart: b.bstart || '',
        bend: b.bend || '',
        btheatre: b.btheatre || '',
        bsurgeon: b.bsurgeon || '',
        bsurgeon_id: b.bsurgeon_id || null,
        bnotes: b.bnotes || '',
        patients: b.patients || [],
      },
    });

  // A session's patients are saved alongside it (for a single session, not a repeat).
  const patientPayload = (list) => (list || []).map((p) => ({
    patient_id: p.patient_id,
    planned_procedure: p.planned_procedure || null,
    auth_number: (p.auth_number || '').replace(/\s+/g, '').toUpperCase() || null, // the scheme's authorisation number
  }));

  const saveDraft = async (draft, dates) => {
    const wanted = patientPayload(draft.patients);
    if (editor.isNew) {
      const created = (await scheduleService.createBlocks({ ...draft, bdates: dates })).map((b) => ({ ...b, patients: [] }));
      if (wanted.length && created.length === 1) created[0].patients = await scheduleService.setPatients(created[0].bid, wanted);
      setData((d) => ({ ...d, blocks: [...d.blocks, ...created] }));
      setToast({ text: created.length > 1 ? `${created.length} sessions added` : 'Session added' });
    } else {
      const updated = await scheduleService.updateBlock(draft.bid, draft);
      const patientsChanged = JSON.stringify(wanted) !== JSON.stringify(patientPayload(editor.draft.patients));
      updated.patients = patientsChanged ? await scheduleService.setPatients(draft.bid, wanted) : editor.draft.patients;
      setData((d) => ({ ...d, blocks: d.blocks.map((b) => (b.bid === updated.bid ? updated : b)) }));
      setToast({ text: 'Session updated' });
    }
    setEditor(null);
  };

  // Bill a patient's case from their booking: the assistant opens with the booking and patient attached.
  // "Quote with assistant" on a booked patient: the assistant makes the quote for that patient.
  const quotePatient = (block, p) => {
    const h = hospitalsById[block.bhid];
    navigate('/assistant', {
      state: {
        quoting: {
          key: `q-${block.bid}-${p.patient_id}-${Date.now()}`,
          bid: block.bid,
          patient_id: p.patient_id,
          patient_name: [p.title, p.first_names, p.surname].filter(Boolean).join(' '),
          patient_dob: p.date_of_birth,
          patient_sex: p.sex,
          payer_text: p.payer === 'private' ? 'Private' : p.scheme_name || null,
          date: block.bdate,
          hospital: h ? h.hshort || h.hname : null,
          surgeon: block.bsurgeon || null,
          planned_procedure: p.planned_procedure || null,
        },
      },
    });
  };

  const billPatient = (block, p) => {
    const h = hospitalsById[block.bhid];
    navigate('/assistant', {
      state: {
        billing: {
          key: `${block.bid}-${p.patient_id}-${Date.now()}`,
          bid: block.bid,
          patient_id: p.patient_id,
          patient_name: [p.title, p.first_names, p.surname].filter(Boolean).join(' '),
          patient_dob: p.date_of_birth,
          patient_sex: p.sex,
          payer_text: p.payer === 'private' ? 'Private' : p.scheme_name || null,
          date: block.bdate,
          hospital: h ? h.hshort || h.hname : null,
          theatre: block.btheatre || null,
          surgeon: block.bsurgeon || null,
          planned_procedure: p.planned_procedure || null,
        },
      },
    });
  };

  const removeBlock = async (bid) => {
    await scheduleService.deleteBlock(bid);
    setData((d) => ({ ...d, blocks: d.blocks.filter((b) => b.bid !== bid) }));
    setEditor(null);
    setToast({ text: 'Session deleted' });
  };

  // Drag & drop: move (optimistic) or, with Ctrl/Alt held, copy.
  const moveBlock = async (block, changes, copy) => {
    const next = { ...block, ...changes };
    const unchanged = next.buid === block.buid && next.bdate === block.bdate && next.bstart === block.bstart;
    if (!copy && unchanged) return;
    try {
      if (copy) {
        const created = await scheduleService.createBlocks({ ...next, bdates: [next.bdate] });
        setData((d) => ({ ...d, blocks: [...d.blocks, ...created] }));
        setToast({ text: 'Session copied' });
      } else {
        setData((d) => ({ ...d, blocks: d.blocks.map((b) => (b.bid === block.bid ? next : b)) }));
        const saved = await scheduleService.updateBlock(block.bid, next);
        setData((d) => ({ ...d, blocks: d.blocks.map((b) => (b.bid === saved.bid ? saved : b)) }));
        setToast({ text: next.buid !== block.buid ? `Session handed to ${doctorName(data.doctors.find((x) => x.uid === next.buid))}` : 'Session moved' });
      }
    } catch (err) {
      setData((d) => ({ ...d, blocks: d.blocks.map((b) => (b.bid === block.bid ? block : b)) }));
      setToast({ text: errText(err), tone: 'error' });
    }
  };

  const copyLastWeek = async () => {
    const from = addDays(weekStart, -7);
    const whose = admin ? 'every doctor’s' : editableDoctors.length > 1 ? 'your and your partners’' : 'your';
    const ok = window.confirm(
      `Copy ${whose} theatre lists, rooms and on-call from ${fmtWeekRange(from)} into ${fmtWeekRange(weekStart)}?\n\n` +
      'Sessions already in this week are kept, and leave is not copied.'
    );
    if (!ok) return;
    try {
      const n = await scheduleService.copyWeek(from, weekStart);
      setToast({ text: n ? `${n} session${n === 1 ? '' : 's'} copied from last week` : 'Nothing new to copy — this week already has last week’s sessions' });
      await load();
    } catch (err) {
      setToast({ text: errText(err), tone: 'error' });
    }
  };

  const addHospital = async (name) => {
    const h = await scheduleService.createHospital(name);
    setData((d) => (d.hospitals.some((x) => x.hid === h.hid)
      ? d
      : { ...d, hospitals: [...d.hospitals, h].sort((a, b) => a.hname.localeCompare(b.hname)) }));
    return h;
  };

  // Keyboard: ← → previous / next (week or day), T today, N new session.
  const keys = useRef({});
  keys.current = { step, goToday, openNew };
  useEffect(() => {
    const onKey = (e) => {
      if (editor || groupsOpen || assistantOpen || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.target instanceof Element && e.target.closest('input, textarea, select, [contenteditable]')) return;
      if (e.key === 'ArrowLeft') keys.current.step(-1);
      else if (e.key === 'ArrowRight') keys.current.step(1);
      else if (e.key === 't' || e.key === 'T') keys.current.goToday();
      else if (e.key === 'n' || e.key === 'N') keys.current.openNew();
      else if (e.key === 'a' || e.key === 'A') setAssistantOpen(true);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editor, groupsOpen, assistantOpen]);

  const isDay = view === 'day';
  const isCurrent = view === 'day' ? focusDay === today : weekStart === startOfWeek(today);
  const hint = {
    roster: 'Click a day to open it · click a doctor to focus on them · click an empty day to add · drag to move, Ctrl-drop to copy',
    timeline: 'Click a day to open it · click or drag down an empty slot to add · drag to move, Ctrl-drop to copy · on-call & leave sit in All day',
    day: 'Click a doctor to focus on them · click or drag down their column to add · drag a session across to hand it to another doctor · ← → change day · T today · N new',
  }[view];

  // The view switch — full labels in the page header, icons only in the Day view's single toolbar.
  const viewSwitch = (compact) => (
    <div className="inline-flex rounded-lg bg-slate-200/70 p-1">
      {VIEWS.map(({ key, label, Icon }) => (
        <button
          key={key}
          onClick={() => changeView(key)}
          title={`${label} view`}
          className={`inline-flex items-center gap-1.5 rounded-md ${compact ? 'px-2 py-1' : 'px-3 py-1.5'} text-sm font-semibold transition ${
            view === key ? 'bg-white text-brand-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Icon className="h-4 w-4" /> {!compact && label}
        </button>
      ))}
    </div>
  );
  const unit = view === 'day' ? 'day' : 'week';
  const viewProps = {
    days,
    doctors: visibleDoctors,
    blocks: visibleBlocks,
    hospitalsById,
    colours,
    conflicts,
    canEdit,
    canCreate,
    me,
    onAdd: openNew,
    onEdit: openEdit,
    onMove: moveBlock,
    onOpenDay: openDay,
    onFocusDoctor: soloDoctor,
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className={`h-full flex flex-col min-w-0 ${isDay ? 'gap-2 p-3 lg:px-4' : 'gap-4 p-6'}`}>
      {/* The Day view gives the page header's space to the schedule: its controls live in the toolbar. */}
      {!isDay && (
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-brand-deep">Theatre Schedule</h1>
          <p className="text-slate-500 mt-1 text-sm">
            {admin
              ? 'Plan every doctor’s theatre lists, rooms and on-call for the week.'
              : 'Map out your week — and see where your partners are.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {viewSwitch(false)}
          <button
            onClick={() => setAssistantOpen(true)}
            title="Speak or type a request (A)"
            className="inline-flex items-center gap-2 rounded-lg border border-brand-teal/60 bg-white px-3.5 py-2 text-sm font-semibold text-brand-navy hover:bg-teal-50"
          >
            <MicrophoneIcon className="h-4 w-4" /> Assistant
          </button>
          <button
            onClick={copyLastWeek}
            disabled={!canCreate}
            title="Repeat last week’s sessions in this week"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 hover:border-brand-navy hover:text-brand-navy disabled:opacity-40"
          >
            <DocumentDuplicateIcon className="h-4 w-4" /> Copy last week
          </button>
          <button
            onClick={() => openNew()}
            disabled={!canCreate}
            title="New session (N)"
            className="inline-flex items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-40"
          >
            <PlusIcon className="h-5 w-5" /> Add session
          </button>
        </div>
      </div>
      )}

      {/* Toolbar: navigation, group, doctor overlay toggles */}
      <div className={`flex flex-wrap items-center rounded-xl border border-slate-200 bg-white shadow-sm ${isDay ? 'gap-x-3 gap-y-1.5 px-2.5 py-1.5' : 'gap-x-4 gap-y-2 px-3 py-2'}`}>
        <div className="flex items-center gap-1">
          {view === 'day' && (
            <button
              onClick={() => changeView(lastWeekView)}
              title="Back to the whole week"
              className="mr-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold text-brand-navy hover:bg-slate-100"
            >
              <ArrowUturnLeftIcon className="h-4 w-4" /> Week
            </button>
          )}
          <button onClick={() => step(-1)} title={`Previous ${unit} (←)`} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100">
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <button
            onClick={goToday}
            title="Today (T)"
            className={`rounded-md px-2.5 py-1 text-sm font-semibold ${isCurrent ? 'text-slate-400' : 'text-brand-navy hover:bg-slate-100'}`}
          >
            Today
          </button>
          <button onClick={() => step(1)} title={`Next ${unit} (→)`} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100">
            <ChevronRightIcon className="h-5 w-5" />
          </button>
          <span className="ml-2 whitespace-nowrap font-semibold text-brand-deep">
            {view === 'day'
              ? fromISO(focusDay).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
              : fmtWeekRange(weekStart)}
          </span>
        </div>

        {view === 'day' && (
          <div className="flex items-center gap-0.5">
            {days.map((d) => (
              <button
                key={d}
                onClick={() => setFocusDay(d)}
                className={`rounded-md px-2 py-1 text-xs font-semibold transition ${
                  d === focusDay ? 'bg-brand-navy text-white' : d === today ? 'text-brand-teal hover:bg-slate-100' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                {fmtDayName(d).slice(0, 2)} {fmtDayNum(d)}
              </button>
            ))}
          </div>
        )}

        <div className="hidden h-6 w-px bg-slate-200 md:block" />

        <div className="flex items-center gap-2">
          <select
            value={scope}
            onChange={(e) => { setScope(e.target.value); setHidden(new Set()); }}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-700 outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40"
          >
            <option value="all">Everyone</option>
            {iAmDoctor && <option value="me">Just me</option>}
            {data.groups.map((g) => (
              <option key={g.gid} value={`g${g.gid}`}>{g.gname}</option>
            ))}
          </select>
          {admin && (
            <button
              onClick={() => setGroupsOpen(true)}
              title="Partnership & coverage groups"
              className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold text-slate-500 hover:bg-slate-100 hover:text-brand-navy"
            >
              <UserGroupIcon className="h-4 w-4" /> Groups
            </button>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {isDay && !focused ? (
            // Doctors are the Day view's column headings, so their toggles fold into one dropdown.
            <DoctorFilter
              doctors={scopedDoctors}
              hidden={hidden}
              colours={colours}
              me={me}
              onToggle={toggleDoctor}
              onSolo={soloDoctor}
              onShowAll={() => setHidden(new Set())}
            />
          ) : focused ? (
            <button
              onClick={() => setHidden(new Set())}
              title="Show every doctor again"
              className="inline-flex items-center gap-1.5 rounded-full py-1 pl-2.5 pr-2 text-xs font-semibold text-white"
              style={{ background: colours[focused.uid] }}
            >
              Only {doctorName(focused)}
              <span className="inline-flex items-center gap-0.5 rounded-full bg-white/25 px-1.5 py-0.5">
                <XMarkIcon className="h-3 w-3" /> Show everyone
              </span>
            </button>
          ) : (
            <>
              {scopedDoctors.map((d) => {
                const c = colours[d.uid];
                const off = hidden.has(d.uid);
                return (
                  <button
                    key={d.uid}
                    onClick={() => toggleDoctor(d.uid)}
                    onDoubleClick={() => soloDoctor(d.uid)}
                    title={off ? 'Show on the calendar' : 'Hide from the calendar · double-click to show only this doctor'}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                      off ? 'border-slate-200 bg-white text-slate-400' : 'border-transparent text-slate-700'
                    }`}
                    style={off ? undefined : { background: tint(c, 0.14) }}
                  >
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: off ? 'transparent' : c, boxShadow: `inset 0 0 0 2px ${c}` }} />
                    {doctorName(d)}
                    {d.uid === me && <span className="text-slate-400">(you)</span>}
                  </button>
                );
              })}
              {hidden.size > 0 && (
                <button onClick={() => setHidden(new Set())} className="px-1.5 text-xs font-semibold text-brand-mid hover:underline">
                  Show all
                </button>
              )}
            </>
          )}
        </div>

        <div className="ml-auto flex items-center gap-1.5">
          <span title={hint} className="cursor-help rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <InformationCircleIcon className="h-5 w-5" />
          </span>
          {isDay && (
            <>
              {viewSwitch(true)}
              <button
                onClick={() => setAssistantOpen(true)}
                title="Assistant — speak or type a request (A)"
                className="rounded-lg border border-brand-teal/60 bg-white p-1.5 text-brand-navy hover:bg-teal-50"
              >
                <MicrophoneIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => openNew()}
                disabled={!canCreate}
                title="New session (N)"
                className="inline-flex items-center gap-1 rounded-lg bg-brand-gradient px-3 py-1.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-40"
              >
                <PlusIcon className="h-4 w-4" /> Add
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="grid flex-1 place-items-center rounded-xl border border-slate-200 bg-white text-sm text-slate-400">Loading schedule…</div>
      ) : data.doctors.length === 0 ? (
        <EmptyState admin={admin} />
      ) : visibleDoctors.length === 0 ? (
        <div className="grid flex-1 place-items-center rounded-xl border border-dashed border-slate-300 bg-white/60 text-sm text-slate-400">
          Every doctor is hidden — pick one above to show their week.
        </div>
      ) : view === 'roster' ? (
        <RosterView {...viewProps} />
      ) : view === 'timeline' ? (
        <TimelineView {...viewProps} />
      ) : (
        <DayView {...viewProps} day={focusDay} />
      )}


      {editor && (
        <BlockEditor
          initial={editor.draft}
          isNew={editor.isNew}
          doctors={data.doctors}
          hospitals={data.hospitals}
          blocks={data.blocks}
          colours={colours}
          canEdit={canEdit}
          onClose={() => setEditor(null)}
          onSave={saveDraft}
          onDelete={removeBlock}
          onAddHospital={addHospital}
          canBill={(b) => admin || b.buid === me}
          onBill={billPatient}
          onQuoteAssistant={quotePatient}
          onQuoteChanged={load}
        />
      )}

      {groupsOpen && (
        <GroupsModal
          groups={data.groups}
          doctors={data.doctors}
          colours={colours}
          onClose={() => setGroupsOpen(false)}
          onChanged={load}
        />
      )}

      {assistantOpen && <AssistantPanel onClose={() => setAssistantOpen(false)} onExecuted={load} />}

      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow-lg ${
            toast.tone === 'error' ? 'bg-red-600' : 'bg-brand-deep'
          }`}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ admin }) => (
  <div className="grid flex-1 place-items-center rounded-xl border border-dashed border-slate-300 bg-white/60 p-8 text-center">
    <div>
      <div className="mx-auto mb-3 h-12 w-1 rounded bg-brand-gradient" />
      <p className="font-semibold text-slate-600">No doctors in this practice yet</p>
      <p className="mt-1 text-sm text-slate-400">
        {admin ? (
          <>Add doctors in <a href="/users" className="font-semibold text-brand-mid hover:underline">User Management</a> and their weeks will appear here.</>
        ) : (
          'Ask your practice administrator to add the doctors in your practice.'
        )}
      </p>
    </div>
  </div>
);

export default Schedule;
